/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState, useRef, useLayoutEffect } from 'react';
import { Button } from 'reactstrap';
import { CurrentUserData } from '../../reducers/current-user';
import { MentionsInput, Mention, SuggestionDataItem } from 'react-mentions';
import { BlockingLoader } from '@g17eco/atoms/loader';
import './styles.scss';
import { renderSuggestion } from './utils';
import { useAddUtrvCommentMutation } from '../../api/utrv-comments';
import { useSiteAlert } from '../../hooks/useSiteAlert';

interface AddCommentProps {
  users: SuggestionDataItem[];
  toggle: () => void;
  utrvId: string;
  user: CurrentUserData;
  surveyId: string;
}

const AddComment = ({ users, toggle, utrvId, user, surveyId }: AddCommentProps) => {

  const { addSiteError } = useSiteAlert();
  const [comment, setComment] = useState('');
  const textareaRef = useRef<null | HTMLTextAreaElement>(null);
  const [addUtrvComment, { isLoading }] = useAddUtrvCommentMutation();

  const handleSubmit = () => {
    addUtrvComment({ surveyId, utrvId, text: comment })
      .unwrap()
      .then(() => {
        setComment('');
      })
      .catch((e) => {
        addSiteError(e);
      });
  };

  const handleChange = ({ target }: { target: { value: string } }): void => setComment(target.value);

  useLayoutEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'inherit';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [comment]);

  const formValid = comment.length > 0;
  return (
    <>
      {isLoading ? <BlockingLoader /> : null}
      <div className='card mb-2'>
        <div className='card-body'>
          <div className='card-title strong'>{user.name}</div>
          <div className='mt-3 card-text'>
            <MentionsInput
              className='mentions-input mb-3'
              placeholder='Write a comment'
              inputRef={textareaRef}
              value={comment}
              onChange={handleChange}>
              <Mention
                trigger='@'
                data={users}
                className='mention'
                renderSuggestion={renderSuggestion}
                displayTransform={(id, display) => `@${display}`}
                appendSpaceOnAdd={true}
              />
            </MentionsInput>
          </div>
          <div className='mt-3 d-flex justify-content-between'>
            <Button color='link-secondary' onClick={toggle} className='text-ThemeHeadingLight'>Close</Button>
            <Button color='primary' disabled={!formValid} onClick={handleSubmit}>Comment</Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddComment;
